// Base schemas — template-managed. Do not edit; add customisations in ../custom/index.ts instead.
export * from "./notifications";

import { z } from "zod";

export const passwordValidator = z
	.string()
	.min(8, "Password must be at least 8 characters long")
	.max(70, "Password must be less than 72 characters long")
	.refine(
		(p) => /[A-Z]/.test(p),
		"Password must contain at least one uppercase letter",
	)
	.refine(
		(p) => /[a-z]/.test(p),
		"Password must contain at least one lowercase letter",
	)
	.refine((p) => /[0-9]/.test(p), "Password must contain at least one number");

// Auth
export const EmailSchema = z.object({
	email: z.string().email("Invalid email address"),
});

export const LoginSchema = z.object({
	email: z.string().email("Invalid email address"),
	password: z.string().min(1, "Password is required"),
	otp: z.string().min(0).max(6).optional(),
	recoveryCode: z.string().optional(),
});

export const RegisterSchema = z
	.object({
		email: z.string().email("Invalid email address"),
		password: passwordValidator,
		confirmPassword: passwordValidator,
		firstName: z.string().optional(),
		lastName: z.string().optional(),
	})
	.refine((d) => d.password === d.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	});

export const VerifyEmailSchema = EmailSchema.extend({
	token: z.string().length(6),
});

export const EmailSearchSchema = z.object({
	email: z.string().email().optional(),
});

export const PasswordResetSchema = EmailSchema.extend({
	token: z.string().length(36),
	password: passwordValidator,
	confirmPassword: passwordValidator,
}).refine((d) => d.password === d.confirmPassword, {
	message: "Passwords do not match",
	path: ["confirmPassword"],
});

// User
export const UpdateProfileSchema = z.object({
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	emoji: z.string().optional(),
});

export const UpdateEmailSchema = z.object({
	newEmail: z.string().email("Invalid email address"),
});

export const ChangePasswordSchema = z
	.object({
		currentPassword: z.string().min(1, "Current password is required"),
		newPassword: passwordValidator,
		confirmPassword: z.string().min(1, "Please confirm your new password"),
	})
	.refine((d) => d.newPassword === d.confirmPassword, {
		message: "Passwords do not match",
		path: ["confirmPassword"],
	})
	.refine((d) => d.currentPassword !== d.newPassword, {
		message: "New password must differ from current password",
		path: ["newPassword"],
	});

// Teams
export const CreateTeamSchema = z.object({
	name: z.string().min(1, "Team name is required"),
	emoji: z.string().min(1, "Emoji is required"),
	type: z.enum(["personal", "parent", "child"]),
	parentID: z.string().optional(),
	email: z.string().email("Invalid email address").or(z.literal("")).optional(),
	url: z.string().url("Invalid URL").or(z.literal("")).optional(),
});

export const UpdateTeamSchema = z.object({
	name: z.string().min(1, "Team name is required"),
	emoji: z.string(),
	url: z.string().optional(),
	email: z.string().optional(),
});

export const UpdateTeamSettingsSchema = z.object({
	sync_users: z.boolean(),
});

export const InviteTeamMemberSchema = z.object({
	email: z.string().email("Invalid email address"),
	role: z.enum(["admin", "billing", "member"]),
});

export const RemoveTeamMemberSchema = z.object({
	id: z.string(),
});

export const UpdateTeamMemberSchema = z.object({
	id: z.string(),
	role: z.enum(["owner", "admin", "billing", "member"]),
});

// Billing
export const CreateCheckoutSessionSchema = z.object({
	priceID: z.string(),
	productID: z.string(),
	customerID: z.string(),
	addonPriceID: z.string().optional(),
});

// Misc
export const FeedbackSchema = z.object({
	message: z.string().min(1, "Feedback message cannot be empty."),
});

export const PublicFeedbackSchema = z.object({
	email: z
		.string()
		.email("Please enter a valid email address.")
		.optional()
		.or(z.literal("")),
	message: z.string().min(1, "Feedback message cannot be empty."),
});

// Types
export type LoginInput = z.infer<typeof LoginSchema>;
export type RegisterInput = z.infer<typeof RegisterSchema>;
export type EmailInput = z.infer<typeof EmailSchema>;
export type VerifyEmailInput = z.infer<typeof VerifyEmailSchema>;
export type EmailSearchInput = z.infer<typeof EmailSearchSchema>;
export type PasswordResetInput = z.infer<typeof PasswordResetSchema>;
export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>;
export type UpdateEmailInput = z.infer<typeof UpdateEmailSchema>;
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;
export type CreateTeamInput = z.infer<typeof CreateTeamSchema>;
export type UpdateTeamInput = z.infer<typeof UpdateTeamSchema>;
export type UpdateTeamSettingsInput = z.infer<typeof UpdateTeamSettingsSchema>;
export type InviteTeamMemberInput = z.infer<typeof InviteTeamMemberSchema>;
export type RemoveTeamMemberInput = z.infer<typeof RemoveTeamMemberSchema>;
export type UpdateTeamMemberInput = z.infer<typeof UpdateTeamMemberSchema>;
export type CreateCheckoutSessionInput = z.infer<
	typeof CreateCheckoutSessionSchema
>;
export type FeedbackFormData = z.infer<typeof FeedbackSchema>;
export type PublicFeedbackFormData = z.infer<typeof PublicFeedbackSchema>;
